import React, { useState, useEffect } from 'react';
import { not, pipe, test, contains, indexOf, find, propEq, path } from 'ramda';
import {
  getLabelForDevice,
  getDisplayKeys,
  getMasterUnitParam,
  convertUnitInverse,
  ALERT_CONDITIONS,
  guid,
  hasHst,
  WIDGET_CONFIG,
} from '../../common/ambient';
import { ambient } from '@aw_products/ambient-weather-common';
import './AlertForm.less';
import {
  getAlertParamsForDevice,
  TIME_ALERT_PARAM,
  isBatteryParam,
  getBatteryDisplayValue,
  getBatteryAlertConfig,
  isLeakParam,
  getLeakDisplayValue,
  getLeakAlertConfig,
} from '.'
import { calculateNextDate as calcNextDate } from './timeAlertUtils';
import HsThreshold from '../device/HsThreshold';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AwnSwitch from '../common/AwnSwitch';
const { getSuff } = ambient

const conditions = ALERT_CONDITIONS;

const createAlert = () => ({
  id: guid(),
  param: '',
  condition: '',
  threshold: '',
  dirtyThreshold: '',
  unit: ''
});

const TimeBasedAlertForm = ({ timeData, onChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    startDate: new Date(),
    everyXDays: 1,
    recurring: false,
    ...timeData,
    // Ensure startDate is a Date object
    startDate: timeData && timeData.startDate
      ? new Date(timeData.startDate)
      : new Date()
  });
  
  // Helper state for the UI - convert days back to units
  const [intervalValue, setIntervalValue] = useState(() => {
    if (timeData && timeData.everyXDays) {
      if (timeData.everyXDays % 365 === 0) return timeData.everyXDays / 365;
      if (timeData.everyXDays % 30 === 0) return timeData.everyXDays / 30;
      if (timeData.everyXDays % 7 === 0) return timeData.everyXDays / 7;
      return timeData.everyXDays;
    }
    return 1;
  });
  
  const [intervalUnit, setIntervalUnit] = useState(() => {
    if (timeData && timeData.everyXDays) {
      if (timeData.everyXDays % 365 === 0) return 'years';
      if (timeData.everyXDays % 30 === 0) return 'months';
      if (timeData.everyXDays % 7 === 0) return 'weeks';
      return 'days';
    }
    return 'days';
  });

  useEffect(() => {
    onChange(formData);
  }, [formData, onChange]);

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };
  
  const handleIntervalChange = (value, unit) => {
    setIntervalValue(value);
    setIntervalUnit(unit);
    
    // Convert to days
    const multipliers = {
      days: 1,
      weeks: 7,
      months: 30,
      years: 365
    };
    
    const daysValue = value * multipliers[unit];
    setFormData({
      ...formData,
      everyXDays: daysValue
    });
  };

  // Calculate what the next date will be for preview
  const calculateNextDate = () => {
    return calcNextDate(formData.startDate, formData.everyXDays, formData.recurring);
  };

  const nextDatePreview = calculateNextDate();

  return (
    <div className="time-based-alert-form">
      <div className="form-group">
        <label>Alert Name</label>
        <input
          type="text"
          className="form-control"
          placeholder="Enter alert name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />
      </div>
      
      <div className="form-group">
        <label>Message</label>
        <textarea
          className="form-control"
          placeholder="Enter alert message"
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          rows="3"
        />
      </div>
      
      <div className="form-group">
        <label>Start Date</label>
        <DatePicker
          selected={formData.startDate}
          onChange={(date) => handleChange('startDate', date)}
          className="form-control"
          dateFormat="MMMM d, yyyy"
        />
      </div>
      
      <div className="form-group">
        <label>Alert after</label>
        <div className="input-group">
          <input
            type="number"
            className="form-control"
            min="1"
            value={intervalValue}
            onChange={(e) => handleIntervalChange(parseInt(e.target.value) || 1, intervalUnit)}
          />
          <select
            className="form-control"
            value={intervalUnit}
            onChange={(e) => handleIntervalChange(intervalValue, e.target.value)}
          >
            <option value="days">days</option>
            <option value="weeks">weeks</option>
            <option value="months">months</option>
            <option value="years">years</option>
          </select>
        </div>
        {nextDatePreview && (
          <small className="help-block" style={{ color: '#666', marginTop: '5px' }}>
            {formData.recurring 
              ? `Next alert: ${nextDatePreview.toLocaleDateString()}`
              : `Alert date: ${nextDatePreview.toLocaleDateString()}`
            }
          </small>
        )}
      </div>
      
      <div className="form-group recurring-switch">
        <label>
          <AwnSwitch 
            checked={formData.recurring}
            onChange={(checked) => handleChange('recurring', checked)}
          />
          <span style={{ marginLeft: '10px' }}>Recurring Alert</span>
        </label>
      </div>
    </div>
  );
};

const AlertForm = ({ currentDevice, onChange, alert, onCancel }) => {
  const [newAlert, setNewAlert] = useState(alert || createAlert());
  const [zone, setZone] = useState('');
  const [timeData, setTimeData] = useState(path(['data'], alert) || {});
  const [alertType, setAlertType] = useState(() => {
    // Initialize based on existing alert
    if (alert && alert.param === TIME_ALERT_PARAM) {
      return 'time';
    }
    return 'data';
  });
  const [batteryValue, setBatteryValue] = useState(() => {
    // Initialize battery value from existing alert
    if (alert && isBatteryParam(alert.param) && alert.condition && alert.threshold !== undefined) {
      return getBatteryDisplayValue(alert.param, alert.condition, alert.threshold, currentDevice);
    }
    return 'Low'; // Default to Low
  });
  const [leakValue, setLeakValue] = useState(() => {
    // Initialize leak value from existing alert
    if (alert && isLeakParam(alert.param) && alert.condition && alert.threshold !== undefined) {
      return getLeakDisplayValue(alert.condition, alert.threshold);
    }
    return 'OK'; // Default to OK
  });

  // Store configurations for each alert type to preserve when switching
  const [dataAlert, setDataAlert] = useState(() => {
    if (alert && alert.param !== TIME_ALERT_PARAM) {
      return alert;
    }
    return createAlert();
  });

  const [savedTimeData, setSavedTimeData] = useState(() => {
    if (alert && alert.param === TIME_ALERT_PARAM) {
      return path(['data'], alert) || {};
    }
    return {};
  });

  useEffect(() => {
    const _alert = alert || createAlert()
    if (_alert.threshold) {
      _alert.dirtyThreshold = _alert.threshold;
    }
    setNewAlert(_alert);
  }, [alert]);

  // Calculate units and handle unit-related logic
  useEffect(() => {
    const masterParam = getMasterUnitParam(newAlert.param);
    const units = masterParam ? ambient.DATA_SPEC[masterParam].units : [];
    let updatedAlert = { ...newAlert };

    // Set initial unit if none selected
    if (units && (!newAlert.unit || !contains(newAlert.unit, units))) {
      updatedAlert.unit = units[0];
    }

    // Convert threshold based on unit
    updatedAlert.threshold = updatedAlert.dirtyThreshold;
    if (updatedAlert.unit && units && newAlert.dirtyThreshold) {
      const unitIndex = indexOf(updatedAlert.unit, units);
      updatedAlert.threshold = convertUnitInverse(
        masterParam, 
        unitIndex, 
        parseFloat(newAlert.dirtyThreshold)
      );
    }

    // Only update if values changed
    if (JSON.stringify(updatedAlert) !== JSON.stringify(newAlert)) {
      setNewAlert(updatedAlert);
    }
  }, [newAlert.param, newAlert.unit, newAlert.dirtyThreshold, newAlert]);

  const handleParamChange = (event) => {
    const newParam = event.target.value;
    const oldParam = newAlert.param;
    
    // Reset zone if changing away from WBGT
    if (oldParam === ambient.HST.ALERT_ID && newParam !== ambient.HST.ALERT_ID) {
      setZone('');
    }
    
    setNewAlert({
      ...newAlert,
      param: newParam,
      condition: '',
      dirtyThreshold: '',
      threshold: '',
      unit: ''
    });
  };

  const handleConditionChange = (event) => {
    setNewAlert({
      ...newAlert,
      condition: event.target.value
    });
  };

  const handleThresholdChange = (event) => {
    setNewAlert({
      ...newAlert,
      dirtyThreshold: event.target.value
    });
  };

  const handleUnitChange = (event) => {
    setNewAlert({
      ...newAlert,
      unit: event.target.value
    });
  };

  const handleZoneChange = (event) => {
    setZone(event.target.value);
  };

  const handleBatteryValueChange = (event) => {
    setBatteryValue(event.target.value);
  };

  const handleLeakValueChange = (event) => {
    setLeakValue(event.target.value);
  };

  const handleAlertTypeToggle = (type) => {
    // Save current configuration before switching
    if (alertType === 'data' && type === 'time') {
      // Switching from data to time - save current data alert
      setDataAlert(newAlert);
    } else if (alertType === 'time' && type === 'data') {
      // Switching from time to data - save current time configuration
      setSavedTimeData(timeData);
    }

    setAlertType(type);

    if (type === 'time') {
      // Switch to time-based alert
      if (savedTimeData && Object.keys(savedTimeData).length > 0) {
        // Restore previously saved time configuration
        setTimeData(savedTimeData);
        setNewAlert({
          ...createAlert(),
          param: TIME_ALERT_PARAM
        });
      } else {
        // No saved time data, create new
        setNewAlert({
          ...createAlert(),
          param: TIME_ALERT_PARAM
        });
      }
      setZone('');
    } else {
      // Switch to data-based alert
      if (dataAlert && dataAlert.param && dataAlert.param !== TIME_ALERT_PARAM) {
        // Restore previously saved data alert
        setNewAlert(dataAlert);
        setTimeData({});
      } else {
        // No saved data alert, create new
        setNewAlert(createAlert());
        setTimeData({});
      }
    }
  };

  // Handle WBGT guideline logic
  const hstGuideline = () => {
    if (currentDevice && hasHst(currentDevice) && path(['settings', 'hst', 'guideline'], currentDevice)) {
      return ambient.HST.getFinalGuildeline(currentDevice.settings.hst);
    }
    return null;
  };
  
  const finalGuideline = hstGuideline();
  const masterParam = getMasterUnitParam(newAlert.param);
  const units = masterParam ? ambient.DATA_SPEC[masterParam].units : [];
  const isWgbtGuideline = newAlert.param === ambient.HST.ALERT_ID;
  const isTimeBasedAlert = newAlert.param === TIME_ALERT_PARAM;
  const isBattery = isBatteryParam(newAlert.param);
  const isLeak = isLeakParam(newAlert.param);
  
  // Initialize zone if needed - only when param first changes to WBGT guideline
  useEffect(() => {
    if (isWgbtGuideline && finalGuideline && finalGuideline.zones && finalGuideline.zones.length > 0 && !zone) {
      const initialZone = finalGuideline.zones[0].label;
      setZone(initialZone);
    }
  }, [isWgbtGuideline, finalGuideline, zone]);

  if (!currentDevice) {
    return null;
  }
  
  const showSave = newAlert.param &&
    (newAlert.condition || isWgbtGuideline || isTimeBasedAlert || isBattery || isLeak || newAlert.param === 'notReporting') &&
    (newAlert.threshold !== '' && newAlert.threshold !== undefined && newAlert.threshold !== null || newAlert.param === 'notReporting' || isWgbtGuideline || isTimeBasedAlert || isBattery || isLeak);

  return (
    <div className='alert-alert-form'>
      <div className="alert-type-toggle">
        <button 
          className={`toggle-btn ${alertType === 'data' ? 'active' : ''}`}
          onClick={() => handleAlertTypeToggle('data')}
        >
          Weather Alert
        </button>
        <button 
          className={`toggle-btn ${alertType === 'time' ? 'active' : ''}`}
          onClick={() => handleAlertTypeToggle('time')}
        >
          Scheduled Alert
        </button>
      </div>
      <div className="alert-inputs">
        {alertType === 'time' ? (
          <TimeBasedAlertForm
            timeData={timeData}
            onChange={setTimeData}
          />
        ) : (
          <>
            <select
              className="form-control"
              onChange={handleParamChange}
              value={newAlert.param}
            >
              <option value="">Parameter</option>
              {finalGuideline && <option value={ambient.HST.ALERT_ID}>WBGT Guidelines - {finalGuideline.name}</option>}
              {getAlertParamsForDevice(currentDevice)
                .filter(key => key !== TIME_ALERT_PARAM)
                .map(key => (
                <option key={key} value={key}>
                  {getLabelForDevice(key, currentDevice)}
                </option>
              ))}
            </select>
            {newAlert.param && newAlert.param !== 'notReporting' && (
              <div className='conditions-wrap'>
            {isWgbtGuideline && finalGuideline && finalGuideline.zones ? (
              <>
                <select
                  className="form-control condition"
                  onChange={handleZoneChange}
                  value={zone || (finalGuideline.zones[0] && finalGuideline.zones[0].label)}
                >
                  {finalGuideline.zones.map(z => (
                    <option key={z.label} value={z.label}>{z.label}</option>
                  ))}
                </select>
                {zone && (
                  <HsThreshold
                    zone={find(propEq('label', zone), finalGuideline.zones)}
                    guideline={finalGuideline}
                    hst={path(['settings', 'hst', 'guideline'], currentDevice)}
                  />
                )}
              </>
            ) : isBattery ? (
              <>
                <select
                  className="form-control"
                  onChange={handleBatteryValueChange}
                  value={batteryValue}
                >
                  <option value="Low">Low</option>
                  <option value="OK">OK</option>
                </select>
              </>
            ) : isLeak ? (
              <>
                <select
                  className="form-control"
                  onChange={handleLeakValueChange}
                  value={leakValue}
                >
                  <option value="OK">OK</option>
                  <option value="LEAK">LEAK</option>
                  <option value="OFFLINE">OFFLINE</option>
                </select>
              </>
            ) : (
              <>
                <select
                  className="form-control condition"
                  onChange={handleConditionChange}
                  value={newAlert.condition}
                >
                  <option value="">Condition</option>
                  {conditions.map((arr, i) => (
                    <option key={i} value={arr[1]}>
                      {arr[0]}
                    </option>
                  ))}
                </select>
                <div className="input-group amount">
                  <input
                    type="number"
                    placeholder="Amount"
                    onChange={handleThresholdChange}
                    className="boxy"
                    value={newAlert.dirtyThreshold}
                  />
                  {units && units.length > 0
                    ? (
                      <select
                        className="form-control"
                        onChange={handleUnitChange}
                        value={newAlert.unit}
                      >
                        {units.map(unit => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    )
                    : (
                      <span className='unit-placeholder'>{getSuff(newAlert.param)}</span>
                    )
                  }
                </div>
              </>
            )}
              </div>
            )}
          </>
        )}
      </div>
      <div className='btns'>
        {showSave && (
          <a onClick={() => {
            const alertData = {
              ...newAlert,
              deviceId: currentDevice._id,
              id: guid() // if it's changed, it's a new alert
            };

            // Add WBGT guideline data if applicable
            if (isWgbtGuideline && finalGuideline) {
              alertData.data = {
                zone,
                guideline: finalGuideline
              };
            }

            // Add time-based alert data if applicable
            if (isTimeBasedAlert) {
              const nextDate = calcNextDate(timeData.startDate, timeData.everyXDays, timeData.recurring);

              alertData.data = {
                ...timeData,
                nextDate: nextDate
              };
            }

            // Add battery alert data if applicable
            if (isBattery) {
              const batteryConfig = getBatteryAlertConfig(batteryValue, newAlert.param, currentDevice);
              alertData.condition = batteryConfig.condition;
              alertData.threshold = batteryConfig.threshold;
              alertData.dirtyThreshold = batteryConfig.threshold;
            }

            // Add leak alert data if applicable
            if (isLeak) {
              const leakConfig = getLeakAlertConfig(leakValue);
              alertData.condition = leakConfig.condition;
              alertData.threshold = leakConfig.threshold;
              alertData.dirtyThreshold = leakConfig.threshold;
            }

            onChange(alertData);
            setNewAlert(createAlert());
            setTimeData({});
            setBatteryValue('Low');
            setLeakValue('OK');
          }}>{alert ? 'Apply Changes' : 'Add Alert'}</a>
        )}
        {onCancel && <a onClick={onCancel}>Cancel</a>}
      </div>
    </div>
  );
};

export default AlertForm;
AlertForm.displayName = 'AlertForm'
